import type { HeatDemandData, SystemDetailsData, WaterData, EmittersData } from './types';

export type CylinderRetention = 'retain' | 'replace' | 'none';
export type CylinderVariant = 'standard-vented' | 'standard-unvented' | 'mixergy-vented' | 'mixergy-unvented' | 'none';

/** Heat source family — used for grouping options in the UI */
export type HeatSourceFamily = 'gas' | 'heat-pump';

/** DHW architecture — combi (no cylinder) or stored (has cylinder) */
export type DHWArch = 'combi' | 'stored';

/** Cylinder class — standard or Mixergy upgrade */
export type CylinderClass = 'standard' | 'mixergy';

/** Cylinder pressure — vented or unvented */
export type CylinderPressure = 'vented' | 'unvented';

export interface EvaluationContext {
  optionId: string;
  family: 'regular' | 'system' | 'combi' | 'storage-combi' | 'ashp' | 'gshp';
  /** Heat source grouping for UI display */
  heatSourceFamily: HeatSourceFamily;
  /** DHW architecture: combi (no cylinder) or stored (has cylinder) */
  dhwArchitecture: DHWArch;
  /** Cylinder pressure type (only when stored DHW) */
  cylinderPressure: CylinderPressure | null;
  /** Cylinder class — standard or Mixergy (only when stored DHW) */
  cylinderClass: CylinderClass | null;
  cylinderRetention: CylinderRetention;
  cylinderType: CylinderVariant;
  controls: 'basic' | 'loadComp' | 'weatherComp';
  zoning: 'yPlan' | 'sPlan';
  primariesMm: 22 | 28;
  annualDemandKnown: boolean;
  lowTempReady: boolean;
  /** Whether the existing cylinder is being replaced — based on user choice, not just like-for-like detection */
  replacingCylinder: boolean;
}

const DEFAULT_FUTURE_COMPONENTS = {
  controls: 5,
  zoning: 8,
  distribution: 6,
  cylinder: 6,
};


const DEFAULT_STANDING_LOSS_BY_BAND: Record<WaterData['standingLossBand'], number> = {
  low: 0.8,
  medium: 1.5,
  high: 2.2,
  unknown: 1.5,
};

function parseStandingLossFromText(text: string): number | null {
  if (!text) return null;
  const numeric = Array.from(text.matchAll(/\d+(?:\.\d+)?/g)).map((m) => Number.parseFloat(m[0])).filter((v) => Number.isFinite(v));
  if (numeric.length === 0) return null;
  if (numeric.length === 1) return numeric[0];
  return (numeric[0] + numeric[1]) / 2;
}

function resolveCurrentStandingLossKwhPerDay(wd: WaterData): { value: number; source: 'measured' | 'estimated_from_band' | 'assumed_default' } {
  if (typeof wd.currentStandingLossKwhPerDay === 'number' && Number.isFinite(wd.currentStandingLossKwhPerDay)) {
    return { value: wd.currentStandingLossKwhPerDay, source: wd.standingLossSource === 'measured' ? 'measured' : 'estimated_from_band' };
  }
  const parsed = parseStandingLossFromText(wd.standingLoss);
  if (typeof parsed === 'number' && Number.isFinite(parsed)) {
    return { value: parsed, source: 'estimated_from_band' };
  }
  return { value: DEFAULT_STANDING_LOSS_BY_BAND[wd.standingLossBand], source: 'assumed_default' };
}

function resolveProposedStandingLossKwhPerDay(ctx: EvaluationContext, wd: WaterData): number {
  if (typeof wd.proposedStandingLossKwhPerDay === 'number' && Number.isFinite(wd.proposedStandingLossKwhPerDay)) {
    return wd.proposedStandingLossKwhPerDay;
  }
  const current = resolveCurrentStandingLossKwhPerDay(wd).value;
  if (ctx.cylinderRetention === 'retain') {
    return current;
  }
  if (ctx.cylinderClass === 'mixergy') {
    return 0.6;
  }
  return 0.8;
}

function scoreStandingLossFromKwhPerDay(kwhPerDay: number): number {
  if (kwhPerDay <= 0.6) return 95;
  if (kwhPerDay <= 0.8) return 90;
  if (kwhPerDay <= 1.2) return 75;
  if (kwhPerDay <= 1.6) return 60;
  if (kwhPerDay <= 2.2) return 45;
  return 35;
}

const normalizeOptionId = (optionId: string): string => optionId.replace(/_/g, '-');

export const parseOptionCylinderType = (optionId: string): CylinderVariant => {
  const normalized = normalizeOptionId(optionId);
  if (normalized === 'combi' || normalized === 'storage-combi') return 'none';
  if (normalized.includes('mixergy-unvented')) return 'mixergy-unvented';
  if (normalized.includes('mixergy-open-vented')) return 'mixergy-vented';
  if (normalized.includes('unvented')) return 'standard-unvented';
  return 'standard-vented';
};

/** Parse the heat source family from an option ID */
export const parseHeatSourceFamily = (optionId: string): HeatSourceFamily => {
  const normalized = normalizeOptionId(optionId);
  if (normalized.startsWith('ashp') || normalized.startsWith('gshp')) return 'heat-pump';
  return 'gas';
};

/** Parse DHW architecture from an option ID */
export const parseDHWArch = (optionId: string): DHWArch => {
  const normalized = normalizeOptionId(optionId);
  if (normalized === 'combi' || normalized === 'storage-combi') return 'combi';
  return 'stored';
};

/** Parse cylinder pressure from an option ID */
export const parseCylinderPressure = (optionId: string): CylinderPressure | null => {
  const normalized = normalizeOptionId(optionId);
  if (normalized === 'combi' || normalized === 'storage-combi') return null;
  if (normalized.includes('unvented')) return 'unvented';
  return 'vented';
};

/** Parse cylinder class from an option ID */
export const parseCylinderClass = (optionId: string): CylinderClass | null => {
  const normalized = normalizeOptionId(optionId);
  if (normalized === 'combi' || normalized === 'storage-combi') return null;
  if (normalized.includes('mixergy')) return 'mixergy';
  return 'standard';
};

export function buildEvaluationContext(
  optionId: string,
  hd: HeatDemandData,
  sd: SystemDetailsData,
  wd: WaterData,
  em: EmittersData,
  isLikeForLike: boolean,
): EvaluationContext {
  const cylinderType = parseOptionCylinderType(optionId);
  const normalizedOptionId = normalizeOptionId(optionId);

  const family: EvaluationContext['family'] =
    normalizedOptionId.startsWith('regular') ? 'regular'
      : normalizedOptionId.startsWith('system') ? 'system'
        : normalizedOptionId === 'combi' ? 'combi'
          : normalizedOptionId === 'storage-combi' ? 'storage-combi'
            : normalizedOptionId.startsWith('ashp') ? 'ashp'
              : 'gshp';

  const controls: EvaluationContext['controls'] =
    sd.temperatureControls === 'smart-load-compensation' ? 'weatherComp'
      : sd.temperatureControls === 'load-compensation' ? 'loadComp'
        : 'basic';

  const annualDemandKnown = (hd.knownHeatLossKw !== undefined && hd.knownHeatLossKw > 0) ||
    (hd.heatLossBand !== 'unknown' && hd.bedrooms !== '' && wd.occupants !== '');

  const dhwArchitecture = parseDHWArch(optionId);
  const cylinderPressure = parseCylinderPressure(optionId);
  const cylinderClass = parseCylinderClass(optionId);
  const heatSourceFamily = parseHeatSourceFamily(optionId);

  // Cylinder retention: if no cylinder, 'none'; if replacing, 'replace'; if retaining existing, 'retain'
  const cylinderRetention: CylinderRetention = cylinderType === 'none' ? 'none' : (isLikeForLike ? 'retain' : 'replace');
  const replacingCylinder = cylinderRetention === 'replace';

  return {
    optionId,
    family,
    heatSourceFamily,
    dhwArchitecture,
    cylinderPressure,
    cylinderClass,
    cylinderType,
    cylinderRetention,
    replacingCylinder,
    controls,
    zoning: sd.controlTopology === 's-plan' ? 'sPlan' : 'yPlan',
    primariesMm: em.primarySizeBand === '28mm' ? 28 : 22,
    annualDemandKnown,
    lowTempReady: em.lowTempCapability === 'yes' || sd.lowTempDesign === 'yes',
  };
}

// ============================================================================
// DERIVED TECHNICAL SCORES — replaces hardcoded numbers in HubPage
// ============================================================================

/**
 * Derive a technical fit score from the evaluation context.
 *
 * This replaces the hardcoded scores like `68`, `60`, `72`, `90`
 * that were scattered through deriveSystemOptions(). Every score
 * component is explainable and traceable.
 */
export function deriveTechnicalFit(
  ctx: EvaluationContext,
  sd: SystemDetailsData,
  wd: WaterData,
  coldMainsAssessed: boolean,
  mainsSupportsUnvented: boolean,
  mainsSupportsCombi: boolean,
  hasCylinderConflict: boolean,
  highHeatLoss: boolean,
): { score: number; components: Record<string, number>; reasons: string[] } {
  const components: Record<string, number> = {};
  const reasons: string[] = [];
  const occupancy = Number.parseInt(wd.occupants, 10);
  const highDhwConcurrency = Number.isFinite(occupancy) && occupancy >= 4;
  const hasSecondaryReturn = wd.circulationMode === 'bronze-return-24-7' || wd.circulationMode === 'bronze-return-timed';
  const hasKeepHot = wd.circulationMode === 'keep-hot';

  // ── Base by architecture ─────────────────────────────────────────
  // Combi: no cylinder, depends entirely on mains
  if (ctx.dhwArchitecture === 'combi') {
    if (!coldMainsAssessed) {
      components.mains = 0;
      reasons.push('Cannot evaluate — cold mains not assessed');
    } else if (hasCylinderConflict) {
      components.mains = 15;
      reasons.push('Bronze secondary return rules out combi');
    } else if (!mainsSupportsCombi) {
      components.mains = 25;
      reasons.push('Cold mains below combi threshold');
    } else {
      components.mains = 65;
      reasons.push('Mains supports on-demand delivery');
    }
    // No standing-loss component for combi architecture (no cylinder).
    components.cylinder_class = 0; // N/A

    if (highDhwConcurrency) {
      components.dhw_concurrency = -20;
      reasons.push('Higher household hot-water concurrency penalises on-demand comfort at peak use');
    }

  // Stored DHW: depends on cylinder type and mains for unvented
  } else {
    // Mains adequacy for unvented
    if (ctx.cylinderPressure === 'unvented') {
      if (!coldMainsAssessed) {
        components.mains = 48;
        reasons.push('Cold mains not assessed — unvented requires measurement');
      } else if (!mainsSupportsUnvented) {
        components.mains = 28;
        reasons.push('Mains insufficient for unvented');
      } else {
        components.mains = 85;
        reasons.push('Mains confirmed for unvented operation');
      }
    } else {
      // Vented — no mains dependency
      components.mains = 60;
      reasons.push('Open vented — no mains pressure dependency');
    }

    // Standing loss — THE key differentiator for cylinder class
    if (ctx.cylinderRetention === 'retain') {
      // Retaining existing cylinder: score from CURRENT standing loss band
      const currentStandingLoss = resolveCurrentStandingLossKwhPerDay(wd);
      components.standing_loss = scoreStandingLossFromKwhPerDay(currentStandingLoss.value);
      reasons.push(`Retaining existing cylinder — standing loss ${currentStandingLoss.value.toFixed(1)} kWh/day (${currentStandingLoss.source.replace('_', ' ')})`);
    } else if (ctx.cylinderClass === 'mixergy') {
      // Mixergy: dramatically lower standing loss
      const proposedStandingLoss = resolveProposedStandingLossKwhPerDay(ctx, wd);
      const currentStandingLoss = resolveCurrentStandingLossKwhPerDay(wd).value;
      components.standing_loss = scoreStandingLossFromKwhPerDay(proposedStandingLoss);
      reasons.push(`Mixergy ${ctx.cylinderPressure} — ${proposedStandingLoss.toFixed(1)} kWh/day (vs current ${currentStandingLoss.toFixed(1)} kWh/day)`);
    } else {
      // Standard new cylinder
      const proposedStandingLoss = resolveProposedStandingLossKwhPerDay(ctx, wd);
      const currentStandingLoss = resolveCurrentStandingLossKwhPerDay(wd).value;
      components.standing_loss = scoreStandingLossFromKwhPerDay(proposedStandingLoss);
      reasons.push(`Standard ${ctx.cylinderPressure} cylinder — ${proposedStandingLoss.toFixed(1)} kWh/day (vs current ${currentStandingLoss.toFixed(1)} kWh/day)`);
    }

    if (hasSecondaryReturn) {
      components.standing_loss += -20;
      reasons.push('Bronze secondary return increases distribution/standing losses (comfort gain is mainly faster tap warm-up)');
    } else if (hasKeepHot) {
      components.standing_loss += -10;
      reasons.push('Keep-hot mode increases fuel use and can raise scaling stress; benefit is mainly faster tap warm-up');
    }

    if (highDhwConcurrency) {
      components.dhw_concurrency = 15;
      reasons.push('Stored DHW architecture supports simultaneous draws in higher-occupancy homes');
    }

    // Cylinder class bonus (Mixergy gets explicit credit)
    components.cylinder_class = ctx.cylinderClass === 'mixergy' ? 15 : 0;
  }

  // ── Circuit compatibility ────────────────────────────────────────
  const circuitTypeKnown = sd.circuitType !== 'unknown';
  if (ctx.family === 'regular') {
    components.circuit = circuitTypeKnown && sd.circuitType === 'open-vented' ? 12 : 0;
    if (sd.circuitType === 'sealed') reasons.push('Would need circuit conversion from sealed to open-vented');
  } else if (ctx.family === 'system') {
    components.circuit = circuitTypeKnown && sd.circuitType === 'sealed' ? 10 : 0;
    if (sd.circuitType === 'open-vented') reasons.push('CH circuit would need sealed conversion');
  } else if (ctx.family === 'ashp' || ctx.family === 'gshp') {
    components.circuit = 5; // Heat pumps work with any circuit
  } else {
    components.circuit = 5; // Combi — any circuit
  }

  // ── Heat pump specifics ──────────────────────────────────────────
  if (ctx.family === 'ashp' || ctx.family === 'gshp') {
    components.renewable = ctx.lowTempReady ? 25 : 10;
    if (highHeatLoss) {
      components.heat_loss_penalty = -10;
      reasons.push('High heat loss reduces COP — fabric improvements recommended');
    }
    if (!ctx.lowTempReady) {
      reasons.push('Emitters not confirmed low-temp ready — may need upgrade');
    } else {
      reasons.push('Low-temp emitters confirmed — good SCOP expected');
    }
  }

  // ── Cylinder retention impact ────────────────────────────────────
  if (ctx.replacingCylinder && ctx.dhwArchitecture === 'stored') {
    reasons.push('New cylinder — modern insulation and performance');
  } else if (ctx.cylinderRetention === 'retain') {
    reasons.push('Retaining existing cylinder — performance limited by current condition');
  }

  // ── Sum components ───────────────────────────────────────────────
  const sum = Object.values(components).reduce((a, b) => a + b, 0);

  // Normalize: mains(0-85) + standing_loss(0-95) + circuit(0-12) + cylinder_class(0-15) + renewable(0-25) + penalties
  // Max theoretical for gas stored = 85+95+12+15 = 207 → scale to 0-100
  // Max theoretical for heat pump = 85+95+5+15+25 = 225 → scale to 0-100
  const maxPossible = ctx.family === 'ashp' || ctx.family === 'gshp'
    ? 225 + (highDhwConcurrency ? 15 : 0)
    : ctx.dhwArchitecture === 'combi'
      ? 90
      : 207 + (highDhwConcurrency ? 15 : 0);
  const score = Math.max(0, Math.min(100, Math.round((sum / maxPossible) * 100)));

  return { score, components, reasons };
}

export function standingLossScore(ctx: EvaluationContext, wd: WaterData): number {
  if (ctx.cylinderType === 'none') return 90;
  const proposedStandingLoss = resolveProposedStandingLossKwhPerDay(ctx, wd);
  return scoreStandingLossFromKwhPerDay(proposedStandingLoss);
}

export function futureProofingScore(ctx: EvaluationContext): number {
  if (ctx.family === 'ashp' || ctx.family === 'gshp') return 95;

  const controls = ctx.controls === 'weatherComp' ? 20 : ctx.controls === 'loadComp' ? 14 : DEFAULT_FUTURE_COMPONENTS.controls;
  const zoning = ctx.zoning === 'sPlan' ? 16 : DEFAULT_FUTURE_COMPONENTS.zoning;
  const distribution = (ctx.primariesMm === 28 ? 10 : 4) + (ctx.lowTempReady ? 12 : 2);
  const cylinder =
    ctx.cylinderType === 'mixergy-unvented' || ctx.cylinderType === 'mixergy-vented' ? 18
      : ctx.cylinderType === 'standard-unvented' ? 12
        : ctx.cylinderType === 'standard-vented' ? DEFAULT_FUTURE_COMPONENTS.cylinder
          : 2;

  const weightedScore = 12 + controls + zoning + distribution + cylinder;
  return Math.max(25, Math.min(82, weightedScore));
}

export function runningCostPriorityScore(ctx: EvaluationContext): number | null {
  if (!ctx.annualDemandKnown) return null;
  if (ctx.family === 'ashp' || ctx.family === 'gshp') return ctx.lowTempReady ? 95 : 70;
  if (ctx.cylinderType === 'mixergy-unvented' || ctx.cylinderType === 'mixergy-vented') return 75;
  if (ctx.family === 'combi' || ctx.family === 'storage-combi') return 60;
  return 65;
}

/**
 * Standing loss description with provenance — shown in UI.
 * Returns null for combi (no cylinder).
 */
export function standingLossDescription(ctx: EvaluationContext, wd: WaterData): string | null {
  if (ctx.dhwArchitecture === 'combi') return null;

  const current = resolveCurrentStandingLossKwhPerDay(wd).value;
  const proposed = resolveProposedStandingLossKwhPerDay(ctx, wd);

  if (ctx.cylinderRetention === 'retain') {
    return `${proposed.toFixed(1)} kWh/day (vs current ${current.toFixed(1)} kWh/day)`;
  }

  if (ctx.cylinderClass === 'mixergy') {
    return `Mixergy ${ctx.cylinderPressure} — ${proposed.toFixed(1)} kWh/day (vs current ${current.toFixed(1)} kWh/day)`;
  }

  return `${proposed.toFixed(1)} kWh/day (vs current ${current.toFixed(1)} kWh/day)`;
}

/**
 * Cylinder replacement description — what changes when replacing.
 */
export function cylinderRetentionDescription(ctx: EvaluationContext): string | null {
  if (ctx.dhwArchitecture === 'combi') return null;
  if (ctx.cylinderRetention === 'retain') {
    return 'Existing cylinder retained — standing losses unchanged from current system';
  }
  if (ctx.cylinderClass === 'mixergy') {
    return 'New Mixergy smart cylinder — stratified heating reduces standing losses by ~70%';
  }
  return 'New standard cylinder — modern factory insulation';
}
